import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Settings, LogOut, Search, X, User, Save, Sparkles, Calendar, Shield, Star, Cpu, ImageIcon, Coins, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { getDashboardStats } from '@/lib/dashboard-service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { getModelDisplayName } from '@/services/modelFetchService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountrySelector } from '@/components/CountrySelector';
import { updateUserProfile } from '@/lib/user-service';
import { Badge } from '@/components/ui/badge';

interface ModernHeaderProps {
  isVisible: boolean;
  onSearchOpen: () => void;
  onAddRecipe: () => void;
  onManage: () => void;
  onSignOut: () => void;
  userEmail?: string;
  userRole?: string;
  onHome?: () => void;
  isOnManagePage?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onOpenStudio?: () => void;
  isAdmin?: boolean;
}

const ModernHeader: React.FC<ModernHeaderProps> = ({
  onSearchOpen,
  onAddRecipe,
  onManage,
  onSignOut,
  userEmail: _userEmail,
  userRole: _userRole,
  onHome,
  isOnManagePage = false,
  searchQuery = '',
  onSearchChange,
  onOpenStudio,
  isAdmin: _isAdmin
}) => {
  const { user, userProfile, refreshProfile, canCreateRecipes, chefcoins_balance } = useAuth();
  const navigate = useNavigate();

  const [showProfileViewModal, setShowProfileViewModal] = useState(false);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);

  const [profileName, setProfileName] = useState(userProfile?.name || '');
  const [profileCountry, setProfileCountry] = useState(userProfile?.country || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  interface SimpleLLMSettings {
    textModel: string;
    imageModel: string;
    imageProvider: 'venice' | 'gemini';
  }
  const [adminLLMSettings, setAdminLLMSettings] = useState<SimpleLLMSettings | null>(null);
  const [systemTokensUsed, setSystemTokensUsed] = useState<number | null>(null);
  const [loadingAdminData, setLoadingAdminData] = useState(false);

  useEffect(() => {
    if (showProfileViewModal && userProfile?.role === 'admin' && !adminLLMSettings) {
      setLoadingAdminData(true);
      Promise.all([
        supabase.from('app_settings').select('value').eq('key', 'llm_settings').single(),
        getDashboardStats()
      ]).then(([settingsResult, stats]) => {
        if (settingsResult.data?.value) {
          const parsed = typeof settingsResult.data.value === 'string'
            ? JSON.parse(settingsResult.data.value)
            : settingsResult.data.value;
          setAdminLLMSettings({
            textModel: parsed.textModel || parsed.textGenerationModel || 'Loading...',
            imageModel: parsed.imageModel || parsed.imageGenerationModel || 'Loading...',
            imageProvider: parsed.imageProvider || parsed.imageGenerationProvider || 'venice'
          });
        }
        setSystemTokensUsed(stats.totalTokensUsed);
      }).catch(() => undefined)
        .finally(() => setLoadingAdminData(false));
    }
  }, [showProfileViewModal, userProfile?.role, adminLLMSettings]);

  const handleAddRecipe = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAddRecipe();
  }, [onAddRecipe]);

  const handleManage = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onManage();
  }, [onManage]);

  const handleSignOut = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSignOut();
  }, [onSignOut]);

  const handleOpenStudio = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenStudio?.();
  }, [onOpenStudio]);

  const handleOpenProfileView = () => {
    setShowProfileViewModal(true);
  };

  const handleSearchKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      (e.currentTarget as HTMLInputElement).blur();
    }
  }, []);

  const handleOpenProfileEdit = () => {
    setShowProfileViewModal(false);
    setProfileName(userProfile?.name || '');
    setProfileCountry(userProfile?.country || '');
    setShowProfileEditModal(true);
  };

  const handleSaveProfile = React.useCallback(async () => {
    if (!user) return;

    setIsUpdatingProfile(true);
    try {
      await updateUserProfile(user.id, {
        name: profileName.trim(),
        country: profileCountry
      });
      await refreshProfile();
      setShowProfileEditModal(false);
    } catch {
    } finally {
      setIsUpdatingProfile(false);
    }
  }, [user, profileName, profileCountry, refreshProfile]);

  const memberSince = useMemo(() => {
    return userProfile?.created_at ? format(new Date(userProfile.created_at), 'MMMM d, yyyy') : 'N/A';
  }, [userProfile?.created_at]);

  const subscriptionBadge = useMemo(() => {
    const subscription = userProfile?.subscription || 'free';
    switch (subscription) {
      case 'premium':
        return <Badge variant="default" className="bg-terracotta-500 text-white hover:bg-terracotta-600"><Star className="h-3 w-3 mr-1.5" />Premium</Badge>;
      case 'standard':
        return <Badge variant="secondary"><Star className="h-3 w-3 mr-1.5" />Standard</Badge>;
      case 'banned':
        return <Badge variant="destructive">Banned</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  }, [userProfile?.subscription]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF7F2]/85 dark:bg-[#1A1714]/85 backdrop-blur-xl border-b border-stone-200/50 dark:border-stone-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-[70px]">

            {/* Logo */}
            <div className="flex items-center flex-shrink-0 self-center">
              <button
                onClick={() => {
                  if (isOnManagePage && onHome) {
                    onHome();
                  } else {
                    navigate('/');
                  }
                }}
                className="h-10 flex items-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-terracotta-500/50 rounded-lg px-1"
                aria-label="Go to home"
              >
                <img
                  src="/Cheflab.png"
                  alt="Cheflab"
                  className="h-8 sm:h-9 w-auto"
                />
              </button>
            </div>

            {/* Desktop search field */}
            <div className="hidden md:flex flex-1 max-w-[420px] min-w-0 mx-2 self-center">
              <div className="relative w-full group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="h-10 w-full pl-9 pr-9 rounded-full bg-stone-100/70 dark:bg-stone-800/70 border border-transparent hover:border-stone-200 dark:hover:border-stone-700 focus:outline-none focus:ring-2 focus:ring-terracotta-500/30 focus:border-terracotta-500 focus:bg-white dark:focus:bg-stone-900 text-sm text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => onSearchChange?.('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-200/60 dark:hover:bg-stone-700/60 transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-2 sm:gap-3 ml-auto self-center">

              {/* Hero CTA - Create with Studio */}
              {onOpenStudio && (
                <Button
                  size="sm"
                  onClick={handleOpenStudio}
                  className="hidden md:inline-flex h-10 bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium shadow-sm border-0 rounded-full px-5 transition-all hover:shadow-md hover:-translate-y-px active:translate-y-0 active:scale-[0.98]"
                  title="Create a new recipe with AI assistance"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Create with Studio
                </Button>
              )}

              {/* Manual add recipe button (desktop) */}
              {canCreateRecipes() && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleAddRecipe}
                  className="hidden md:inline-flex h-10 w-10 rounded-full text-stone-600 dark:text-stone-300 hover:bg-stone-100/60 dark:hover:bg-stone-800/60 transition-colors"
                  title="Add a recipe manually"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              )}

              {/* Mobile: search trigger button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onSearchOpen}
                className="md:hidden h-10 w-10 rounded-full hover:bg-stone-100/60 dark:hover:bg-stone-800/60"
                aria-label="Open search"
              >
                <Search className="h-5 w-5" />
              </Button>

              {/* ChefCoin display */}
              <div
                className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-full bg-amber-50 dark:bg-amber-900/15 border border-amber-200/40 dark:border-amber-800/30 shrink-0"
                title="Your ChefCoins balance"
              >
                <Coins className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={2.25} />
                <span className="text-sm font-semibold text-amber-700 dark:text-amber-400 tabular-nums">
                  {chefcoins_balance !== null ? chefcoins_balance.toLocaleString() : '--'}
                </span>
              </div>

              {/* Desktop Manage button - admin / creator access to /manage */}
              {canCreateRecipes() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleManage}
                  className={`hidden md:inline-flex h-10 rounded-full px-3.5 text-stone-600 dark:text-stone-300 hover:bg-stone-100/60 dark:hover:bg-stone-800/60 transition-colors ${isOnManagePage ? 'bg-stone-100 dark:bg-stone-800' : ''
                    }`}
                  title="Manage recipes and admin tools"
                >
                  <Settings className="h-4 w-4 mr-2 opacity-70" />
                  Manage
                </Button>
              )}

              {/* User avatar dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1.5 p-0.5 h-10 hover:bg-stone-100/60 dark:hover:bg-stone-800/60 rounded-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500/50 transition-colors"
                    aria-label="User menu"
                  >
                    <Avatar className="h-9 w-9 ring-2 ring-transparent group-hover:ring-stone-200 dark:group-hover:ring-stone-700 transition-all">
                      <AvatarImage src={user?.user_metadata?.avatar_url} />
                      <AvatarFallback className="bg-gradient-to-br from-terracotta-400 to-terracotta-600 text-white text-sm font-medium">
                        {userProfile?.email?.charAt(0).toUpperCase() || userProfile?.name?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="hidden xl:block h-3.5 w-3.5 text-stone-400 group-hover:text-stone-600 dark:group-hover:text-stone-300 transition-colors" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-64 p-1.5 rounded-xl bg-white dark:bg-stone-900 border-stone-200/80 dark:border-stone-800 shadow-xl"
                >
                  {/* User identity block */}
                  <div className="px-3 py-2.5 mb-1 rounded-lg bg-stone-50 dark:bg-stone-800/60">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-9 w-9 ring-1 ring-stone-200 dark:ring-stone-700">
                        <AvatarImage src={user?.user_metadata?.avatar_url} />
                        <AvatarFallback className="bg-gradient-to-br from-terracotta-400 to-terracotta-600 text-white text-xs font-medium">
                          {userProfile?.email?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-900 dark:text-stone-100 truncate">
                          {userProfile?.name || user?.user_metadata?.full_name || userProfile?.email?.split('@')[0]}
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                          {userProfile?.email}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">
                        <Coins className="h-3 w-3" />
                        {chefcoins_balance !== null ? chefcoins_balance.toLocaleString() : '--'} ChefCoins
                      </span>
                      {subscriptionBadge}
                    </div>
                  </div>

                  <DropdownMenuItem
                    onClick={handleOpenProfileView}
                    className="py-2 px-3 rounded-lg cursor-pointer text-sm focus:bg-stone-100 dark:focus:bg-stone-800"
                  >
                    <User className="h-4 w-4 mr-2.5 text-stone-500" />
                    View Profile
                  </DropdownMenuItem>

                  {canCreateRecipes() && (
                    <DropdownMenuItem
                      onClick={handleAddRecipe}
                      className="py-2 px-3 rounded-lg cursor-pointer text-sm focus:bg-stone-100 dark:focus:bg-stone-800 md:hidden"
                    >
                      <Plus className="h-4 w-4 mr-2.5 text-stone-500" />
                      Add Recipe
                    </DropdownMenuItem>
                  )}

                  {canCreateRecipes() && (
                    <DropdownMenuItem
                      onClick={handleManage}
                      className={`py-2 px-3 rounded-lg cursor-pointer text-sm focus:bg-stone-100 dark:focus:bg-stone-800 md:hidden ${isOnManagePage ? 'bg-stone-100 dark:bg-stone-800' : ''}`}
                    >
                      <Settings className="h-4 w-4 mr-2.5 text-stone-500" />
                      Manage
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onClick={handleOpenProfileEdit}
                    className="py-2 px-3 rounded-lg cursor-pointer text-sm focus:bg-stone-100 dark:focus:bg-stone-800"
                  >
                    <User className="h-4 w-4 mr-2.5 text-stone-500" />
                    Edit Profile
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="my-1 bg-stone-200/70 dark:bg-stone-800" />

                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="py-2 px-3 rounded-lg cursor-pointer text-sm text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-900/20 focus:text-red-700 dark:focus:text-red-300"
                  >
                    <LogOut className="h-4 w-4 mr-2.5" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <Dialog open={showProfileViewModal} onOpenChange={setShowProfileViewModal}>
        <DialogContent className="sm:max-w-lg p-0 bg-white dark:bg-[#1A1714] border-stone-200 dark:border-stone-800 rounded-2xl overflow-hidden">
          <div className="relative p-6 sm:p-8">
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-4 right-4 z-20 h-8 w-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800">
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>

            {/* Profile header */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
              <Avatar className="h-20 w-20 ring-4 ring-white dark:ring-stone-800 shadow-lg">
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="text-3xl bg-gradient-to-br from-terracotta-400 to-terracotta-600 text-white font-medium">
                  {userProfile?.email?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-center sm:text-left flex flex-col items-center sm:items-start">
                <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                  <DialogTitle className="text-xl font-semibold text-stone-900 dark:text-white">
                    {userProfile?.name || 'User Profile'}
                  </DialogTitle>
                  {userProfile?.role === 'admin' && (
                    <Badge className="bg-terracotta-100 text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-400 border-0">
                      <Shield className="h-3 w-3 mr-1" />
                      Admin
                    </Badge>
                  )}
                </div>
                <DialogDescription className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                  {userProfile?.email}
                </DialogDescription>
                <div className="flex items-center gap-1.5 mt-2 text-xs text-stone-400 dark:text-stone-500">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Joined {memberSince}</span>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              {/* ChefCoin Balance */}
              <div className="flex flex-col items-center justify-center p-4 bg-terracotta-50 dark:bg-terracotta-900/20 rounded-xl border border-terracotta-100 dark:border-terracotta-900/30">
                <span className="text-xs font-medium text-terracotta-600 dark:text-terracotta-400 mb-1.5">ChefCoins</span>
                <div className="flex items-center gap-2">
                  <img src="/chefcoin.svg" alt="ChefCoin" className="h-6 w-6" />
                  <span className="text-2xl font-bold text-terracotta-700 dark:text-terracotta-300 tabular-nums">
                    {chefcoins_balance !== null ? chefcoins_balance : '--'}
                  </span>
                </div>
              </div>

              {/* Subscription */}
              <div className="flex flex-col items-center justify-center p-4 bg-stone-50 dark:bg-stone-800/50 rounded-xl border border-stone-100 dark:border-stone-800">
                <span className="text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">Plan</span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize
                  ${userProfile?.subscription === 'premium'
                    ? 'bg-terracotta-100 text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-300'
                    : userProfile?.subscription === 'standard'
                      ? 'bg-sage-100 text-sage-700 dark:bg-sage-900/30 dark:text-sage-300'
                      : userProfile?.subscription === 'banned'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-stone-100 text-stone-600 dark:bg-stone-700 dark:text-stone-300'}`
                }>
                  <Star className="h-3.5 w-3.5 mr-1.5" />
                  {userProfile?.subscription ? userProfile.subscription.charAt(0).toUpperCase() + userProfile.subscription.slice(1) : 'Free'}
                </span>
              </div>
            </div>

            {/* Admin Model Insights - Only visible to admins */}
            {userProfile?.role === 'admin' && (
              <div className="mt-4 p-5 rounded-xl border border-terracotta-200 dark:border-terracotta-800 bg-terracotta-50 dark:bg-terracotta-950/30">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-terracotta-600 dark:text-terracotta-400" />
                  <span className="text-base font-semibold text-terracotta-800 dark:text-terracotta-200">Active AI Models</span>
                </div>

                {loadingAdminData ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-terracotta-300 border-t-terracotta-600" />
                  </div>
                ) : adminLLMSettings ? (
                  <div className="space-y-4">
                    {/* Text Model */}
                    <div className="p-3 rounded-lg bg-white/60 dark:bg-stone-800/60">
                      <div className="flex items-center gap-2 mb-1">
                        <Cpu className="h-4 w-4 text-terracotta-600 dark:text-terracotta-400" />
                        <span className="text-sm font-medium text-stone-700 dark:text-stone-200">Text Generation</span>
                      </div>
                      <p className="text-lg font-semibold text-stone-900 dark:text-white">
                        {getModelDisplayName(adminLLMSettings.textModel)}
                      </p>
                      <p className="text-sm text-stone-500 dark:text-stone-400 font-mono mt-0.5">
                        {adminLLMSettings.textModel}
                      </p>
                    </div>

                    {/* Image Provider */}
                    <div className="p-3 rounded-lg bg-white/60 dark:bg-stone-800/60">
                      <div className="flex items-center gap-2 mb-1">
                        <ImageIcon className="h-4 w-4 text-terracotta-600 dark:text-terracotta-400" />
                        <span className="text-sm font-medium text-stone-700 dark:text-stone-200">Image Generation</span>
                      </div>
                      <p className="text-lg font-semibold text-stone-900 dark:text-white">
                        Venice AI
                      </p>
                      <p className="text-sm text-stone-500 dark:text-stone-400 font-mono mt-0.5">
                        {adminLLMSettings.imageModel}
                      </p>
                    </div>

                    {/* System Token Usage */}
                    {systemTokensUsed !== null && (
                      <div className="p-3 rounded-lg bg-white/60 dark:bg-stone-800/60">
                        <span className="text-sm font-medium text-stone-700 dark:text-stone-200">System-wide Token Usage</span>
                        <p className="text-2xl font-bold text-terracotta-600 dark:text-terracotta-400 tabular-nums">
                          {systemTokensUsed > 1000 ? `${(systemTokensUsed / 1000).toFixed(1)}K` : systemTokensUsed}
                        </p>
                      </div>
                    )}

                    {/* Configure Link */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowProfileViewModal(false);
                        navigate('/manage/llm');
                      }}
                      className="w-full mt-2 border-terracotta-200 dark:border-terracotta-800 text-terracotta-700 dark:text-terracotta-300 hover:bg-terracotta-100 dark:hover:bg-terracotta-900/30"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Configure Models
                    </Button>
                  </div>
                ) : null}
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                onClick={handleOpenProfileEdit}
                className="flex-1 h-10 rounded-lg border-stone-200 dark:border-stone-700"
              >
                <User className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant="ghost"
                onClick={handleSignOut}
                className="flex-1 h-10 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showProfileEditModal} onOpenChange={setShowProfileEditModal}>
        <DialogContent className="sm:max-w-md p-0 bg-white dark:bg-[#1A1714] border-stone-200 dark:border-stone-800 rounded-2xl overflow-hidden">
          <div className="relative p-6">
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="absolute top-4 right-4 z-20 h-8 w-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800">
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>

            <DialogHeader className="mb-6">
              <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-stone-900 dark:text-white">
                <User className="h-5 w-5" />
                Edit Profile
              </DialogTitle>
              <DialogDescription className="text-sm text-stone-500 dark:text-stone-400">
                Update your display name and country.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="profile-name" className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  Display Name
                </label>
                <Input
                  id="profile-name"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full h-11 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg focus:ring-2 focus:ring-terracotta-500 focus:border-terracotta-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  Country
                </label>
                <CountrySelector
                  value={profileCountry}
                  onValueChange={setProfileCountry}
                  placeholder="Select your country"
                  className="w-full h-11 bg-stone-50 dark:bg-stone-800 border-stone-200 dark:border-stone-700 rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowProfileEditModal(false)}
                disabled={isUpdatingProfile}
                className="flex-1 h-10 rounded-lg border-stone-200 dark:border-stone-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={isUpdatingProfile || !profileName.trim()}
                className="flex-1 h-10 rounded-lg bg-terracotta-500 hover:bg-terracotta-600 text-white"
              >
                {isUpdatingProfile ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModernHeader;
